import { useEffect, useState, useRef } from "react";
import { Button } from "./button";
import { Progress } from "./progress";
import { useSpeechSynthesis } from "./useSpeechSynthesis";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Settings,
  Check,
} from "lucide-react";

const SlideVideoPlayer = ({
  sections = [],
  onProgress,
  onComplete,
}) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const hasCompletedRef = useRef(false);

  /* --------------------------------------------------
     SPEECH SYNTHESIS
  -------------------------------------------------- */
  const {
    speak,
    cancel,
    voices,
    selectedVoice,
    setSelectedVoice,
    rate,
    setRate,
    isSupported,
  } = useSpeechSynthesis({
    onEnd: () => {
      nextSlide();
    },
  });

  const slides = sections[currentSectionIndex]?.slides || [];
  const currentSlide = slides[currentSlideIndex];

  /* --------------------------------------------------
     PROGRESS (NO 97% BUG)
  -------------------------------------------------- */
  useEffect(() => {
    if (!sections.length) return;

    const totalSections = sections.length;
    const slidesInSection =
      sections[currentSectionIndex]?.slides?.length || 1;

    const sectionFraction =
      (currentSlideIndex + 1) / slidesInSection;

    const overall =
      ((currentSectionIndex + sectionFraction) / totalSections) * 100;

    const clamped = Math.min(overall, 100);

    setProgress(clamped);
    onProgress?.(clamped);

    const isLastSection =
      currentSectionIndex === totalSections - 1;
    const isLastSlide =
      currentSlideIndex === slidesInSection - 1;

    if (isLastSection && isLastSlide && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      setProgress(100);
      onProgress?.(100);
      onComplete?.();
    }
  }, [
    sections,
    currentSectionIndex,
    currentSlideIndex,
    onProgress,
    onComplete,
  ]);

  /* --------------------------------------------------
     PLAY / PAUSE
  -------------------------------------------------- */
  const startNarration = () => {
    if (!isSupported || !currentSlide?.voiceOverScript) return;
    cancel();
    speak(currentSlide.voiceOverScript);
  };

  const togglePlay = () => {
    if (isPlaying) {
      cancel();
    } else {
      startNarration();
    }
    setIsPlaying(!isPlaying);
  };

  /* --------------------------------------------------
     NAVIGATION
  -------------------------------------------------- */
  const nextSlide = () => {
    cancel();
    setIsPlaying(false);

    if (currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex((p) => p + 1);
    } else if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex((p) => p + 1);
      setCurrentSlideIndex(0);
    }
  };

  const prevSlide = () => {
    cancel();
    setIsPlaying(false);

    if (currentSlideIndex > 0) {
      setCurrentSlideIndex((p) => p - 1);
    } else if (currentSectionIndex > 0) {
      const prevSection = sections[currentSectionIndex - 1];
      setCurrentSectionIndex((p) => p - 1);
      setCurrentSlideIndex(
        (prevSection.slides?.length || 1) - 1
      );
    }
  };

  useEffect(() => {
    return () => cancel();
  }, [cancel]);

  /* --------------------------------------------------
     EMPTY STATE
  -------------------------------------------------- */
  if (!sections.length) {
    return (
      <div className="border rounded-xl p-10 text-center text-gray-500">
        No training content available.
      </div>
    );
  }

  /* --------------------------------------------------
     UI
  -------------------------------------------------- */
  return (
    <div className="space-y-4">

      {/* ================= VIDEO CONTENT ================= */}
    {/* ================= SLIDE PRESENTATION ================= */}
<div
  className="
    relative h-[480px]
    rounded-xl
    bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950
    border border-slate-800
    overflow-hidden
  "
>
  {/* ===== TOP BAR ===== */}
  <div className="absolute top-0 left-0 right-0 px-6 py-4 flex justify-between">
    <div>
      <p className="text-xs tracking-widest text-cyan-400 uppercase">
        Section {currentSectionIndex + 1} of {sections.length}
      </p>
      <h4 className="text-sm font-semibold text-slate-200">
        {sections[currentSectionIndex]?.title}
      </h4>
    </div>

    <div className="flex items-center gap-2 text-slate-300 text-sm">
      <span>Slide {currentSlideIndex + 1}/{slides.length}</span>
    </div>
  </div>

  {/* ===== CENTER CONTENT ===== */}
  <div className="h-full flex items-center justify-center px-16 text-center">
    <div className="max-w-4xl space-y-6">

      <h6 className="text-3xl md:text-4xl font-bold text-white">
        {currentSlide?.title}
      </h6>

      <p className="text-cyan-300 text-base leading-relaxed">
        {currentSlide?.voiceOverScript}
      </p>

    </div>
  </div>

  {/* ===== NAVIGATION ARROWS ===== */}
  <button
    onClick={prevSlide}
    className="
      absolute left-4 top-1/2 -translate-y-1/2
      w-10 h-10 rounded-full
      bg-white/10 hover:bg-white/20
      flex items-center justify-center
      text-white transition
    "
  >
    ‹
  </button>

  <button
    onClick={nextSlide}
    className="
      absolute right-4 top-1/2 -translate-y-1/2
      w-10 h-10 rounded-full
      bg-white/10 hover:bg-white/20
      flex items-center justify-center
      text-white transition
    "
  >
    ›
  </button>

  {/* ===== NARRATING BADGE ===== */}
  {isPlaying && (
    <div className="
      absolute bottom-16 left-6
      flex items-center gap-2
      bg-black/50 px-3 py-1.5 rounded-full
      text-cyan-300 text-xs
    ">
      <span className="w-1.5 h-3 bg-cyan-400 rounded animate-pulse"></span>
      Narrating
    </div>
  )}

  {/* ===== PROGRESS BAR ===== */}
  <div className="absolute bottom-6 left-10 right-10">
    <Progress
      value={progress}
      className="h-2 bg-slate-700 [&>div]:bg-cyan-300"
    />
  </div>
</div>


      {/* ================= CONTROLS (UNDER VIDEO) ================= */}
      <div
        className="
          flex items-center justify-between
          px-4 py-3 bg-white
          rounded-xl shadow-sm
        "
      >
        {/* Left */}
        <div className="flex items-center gap-3">
          <button
            onClick={prevSlide}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <SkipBack size={18} />
          </button>

          <button
            onClick={togglePlay}
            className="
              w-12 h-12 rounded-xl
              bg-teal-500 text-white
              flex items-center justify-center
              hover:bg-teal-600 transition
            "
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>

          <button
            onClick={nextSlide}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <SkipForward size={18} />
          </button>
        </div>

        {/* Right */}
        <div className="flex items-center gap-3 relative">
          <button
            onClick={() => {
              setIsMuted(!isMuted);
              if (!isMuted) cancel();
            }}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>

          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <Settings size={18} />
          </button>

          {/* Settings Panel */}
          {showSettings && (
            <div
              className="
                absolute right-0 top-12 w-72
                bg-white rounded-xl shadow-xl
                p-4 z-50
              "
            >
              <h4 className="font-semibold text-sm mb-3">
                Voice Settings
              </h4>

              <label className="text-xs text-gray-500">
                Narrator Voice
              </label>
              <select
                className="w-full mt-1 p-2 border rounded text-sm"
                value={selectedVoice?.name || ""}
                onChange={(e) => {
                  const v = voices.find(
                    (x) => x.name === e.target.value
                  );
                  if (v) setSelectedVoice(v);
                }}
              >
                {voices
                  .filter((v) => v.lang.startsWith("en"))
                  .map((v) => (
                    <option key={v.name} value={v.name}>
                      {v.name}
                    </option>
                  ))}
              </select>

              <div className="mt-4">
                <label className="text-xs text-gray-500">
                  Speed: {rate}x
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.25"
                  value={rate}
                  onChange={(e) =>
                    setRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>

              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-2">
                  Jump to Section
                </p>

                {sections.map((section, idx) => (
                  <button
                    key={section.slide_id || idx}
                    onClick={() => {
                      setCurrentSectionIndex(idx);
                      setCurrentSlideIndex(0);
                      cancel();
                      setIsPlaying(false);
                      setShowSettings(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm
                      ${
                        idx === currentSectionIndex
                          ? "bg-teal-500 text-white"
                          : "hover:bg-gray-100"
                      }`}
                  >
                    {idx === currentSectionIndex && (
                      <Check size={14} />
                    )}
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= SECTION PILLS ================= */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {sections.map((section, index) => (
          <button
            key={section.slide_id || index}
            onClick={() => {
              setCurrentSectionIndex(index);
              setCurrentSlideIndex(0);
              cancel();
              setIsPlaying(false);
            }}
            className={`
              whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium
              transition-colors
              ${
                index === currentSectionIndex
                  ? "bg-teal-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }
            `}
          >
            {section.title}
          </button>
        ))}
      </div>

      {!isSupported && (
        <p className="text-xs text-cyan-300 opacity-80">
          Voice not supported in this browser
        </p>
      )}
    </div>
  );
};

export default SlideVideoPlayer;
